import { Anchor, Badge, Box, Button, Card, Flex, Group, ScrollArea, Text, Title } from "@mantine/core";
import { IStatbox } from "@mctypes/frontend.types";
import type { IIssueApi } from "@schemas/issueSchema";
import type { ColDef } from "ag-grid-community";
import React, { useMemo, useState } from "react";
import { OptionBadge } from "../../../../../../components";
import ViewEditTable from "../../../../../../components/ViewEditTable/ViewEditTable";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks";
import LocalErrorBoundary from "../../../../../../util/LocalErrorBoundary";
import { StatsBox } from "../../../StatsBox";
import { ISSUE_STATES_OPTIONS } from "../../../issueConstants";
import { selectCategoryMatrices } from "../../../issueRisikoSelectors";
import { selectIssuesWithTypeISSUE } from "../../../issueSelectors";
import { thunkOpenIssueModalById } from "../../../issueSlice";
import { convertValueAndColorToMultiDisplayColor, deduplicateMatrices } from "../../../issueUtils";
import { selectHttpIssueBoard } from "../../../redux/selectors/issueRtkSelectors";
import "./RisikoMatrix.css";

const RisikoDashboard: React.FC = () => {
  const [showAckumulated, setShowAckumulated] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState<IIssueApi[]>([]);

  const issues = useAppSelector(selectIssuesWithTypeISSUE);
  const issueBoard = useAppSelector(selectHttpIssueBoard);

  const boxes: IStatbox[] = [
    {
      icon: "fa-book",
      value: issues.filter((x) => x.archived !== true).length,
      text: `Registrert`,
      classColor: "text-primary",
    },
    {
      icon: "fa-folder-open",
      value: issues.filter((x) => x.status === "Opprettet" || x.status === "I prosess").length,
      text: `Åpne`,
      classColor: "text-primary",
    },
    {
      icon: "fa-check",
      value: issues.filter((x) => {
        if (x.dates.completed == null) return false;
        const completedDate = new Date(x.dates.completed);
        const now = new Date();
        const twoWeeksAgo = new Date(now.setDate(now.getDate() - 14));
        return completedDate >= twoWeeksAgo;
      }).length,
      text: `Lukket siste 14 dager`,
      classColor: "text-success",
    },
    {
      icon: "fa-calendar",
      value: issues.filter((x) => {
        if (x.dates.due == null) return false;
        const dueDate = new Date(x.dates.due);
        const now = new Date();
        const twoWeeksFromNow = new Date(now.setDate(now.getDate() + 14));
        return dueDate <= twoWeeksFromNow;
      }).length,
      text: `Tidsfrist neste 14 dager`,
      classColor: "text-danger",
    },
  ];

  return (
    // Gör HELA sidan scrollbar (föräldrar kan vara h:100%)
    <ScrollArea h="100%" w="100%">
      <Card withBorder p="md" w="100%">
        <Group justify="space-between" align="center" mb="xs">
          <Title order={4}>Risikoer</Title>
          <Badge variant="filled" radius="sm" color="mc-blue">
            {issues.length}
          </Badge>
        </Group>

        {/* Statboxes */}
        <Group justify="flex-start" wrap="nowrap" gap="sm">
          {boxes.map((stat, idx) => (
            <StatsBox key={idx} stat={stat} />
          ))}
        </Group>

        {/* Innehåll: matriser + tabell. Hela kortet kan bli högt; ScrollArea runt allt gör att sidan scrollar */}
        <Flex gap="md" mt="md">
          {/* Vänster: Kategorier */}
          <Card withBorder p="md" flex={1} style={{ minWidth: 0 }}>
            <Group justify="space-between" align="center" mb="xs">
              <Title order={3}>Kategorier</Title>
              <Button
                size="xs"
                variant={showAckumulated ? "filled" : "outline"}
                onClick={() => setShowAckumulated((v) => !v)}
              >
                Akkumulert
              </Button>
            </Group>

            <Text size="sm" c="dimmed" mb="xs">
              <i className="fa fa-info-circle fa-fw" /> Klikk på en celle for å se aktuelle risikoer
            </Text>

            <LocalErrorBoundary>
              {issueBoard.risikoSection != null && (
                <RisikoMatrices ackumulated={showAckumulated} onCellClick={setSelectedIssues} />
              )}
            </LocalErrorBoundary>
          </Card>

          {/* Höger: Risikoer lista */}
          <Card withBorder p="md" flex={1} style={{ minWidth: 0 }}>
            <Box w="100%">
              <RisikoTable issues={selectedIssues.length > 0 ? selectedIssues : issues} />
            </Box>
          </Card>
        </Flex>
      </Card>
    </ScrollArea>
  );
};

export default RisikoDashboard;

const RisikoMatrices: React.FC<{ ackumulated: boolean; onCellClick: (issues: IIssueApi[]) => void }> = ({
  ackumulated,
  onCellClick,
}) => {
  const [selectedCell, setSelectedCell] = useState<[number, number, number] | null>(null);

  const categoryMatrices = useAppSelector(selectCategoryMatrices);

  const ackumulatedMatrices = useMemo(() => {
    if (!ackumulated) return [];
    return deduplicateMatrices(categoryMatrices);
  }, [categoryMatrices, ackumulated]);

  const matrices = ackumulated && ackumulatedMatrices.length > 0 ? ackumulatedMatrices : categoryMatrices;

  return (
    <div>
      {matrices.map((categoryMatrix, index) => (
        <div key={index} style={{ marginBottom: "1rem" }}>
          <Title order={5}>{categoryMatrix.name}</Title>
          <div className="matrix-container">
            {categoryMatrix.matrix.map((row, rowIndex) => (
              <div className="matrix-row" key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <div
                    key={cellIndex}
                    className={`matrix-cell${cell.score > 0 ? " pointer" : ""}${
                      selectedCell != null &&
                      selectedCell[0] === index &&
                      selectedCell[1] === rowIndex &&
                      selectedCell[2] === cellIndex
                        ? " selected-matrix-cell"
                        : ""
                    }`}
                    style={{ backgroundColor: cell.color }}
                    onClick={() => {
                      if (cell.score === 0) return;

                      if (
                        selectedCell != null &&
                        selectedCell[0] === index &&
                        selectedCell[1] === rowIndex &&
                        selectedCell[2] === cellIndex
                      ) {
                        setSelectedCell(null);
                        onCellClick([]);
                        return;
                      }

                      setSelectedCell([index, rowIndex, cellIndex]);
                      onCellClick(cell.issues);
                    }}
                  >
                    {cell.score > 0 && <span className="score">{cell.score}</span>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export const RisikoTable: React.FC<{ issues: IIssueApi[] }> = ({ issues }) => {
  const dispatch = useAppDispatch();

  const columns = useMemo<ColDef<IIssueApi>[]>(
    () => [
      {
        headerName: "ID",
        width: 80,
        field: "id",
        cellRenderer: (params) => (
          <Anchor
            component="button"
            onClick={() => dispatch(thunkOpenIssueModalById(params.data._id))}
            variant="subtle"
          >
            {params.value}
          </Anchor>
        ),
      },
      { minWidth: 120, flex: 1, headerName: "Tittel", field: "title" },
      { minWidth: 120, flex: 1, headerName: "Beskrivelse", field: "description" },
      {
        width: 120,
        headerName: "Opprettet",
        field: "createdAt",
        cellRenderer: (params) => new Date(params.value).toLocaleDateString(),
      },
      {
        width: 120,
        headerName: "Status",
        field: "status",
        cellRenderer: (params) => (
          <OptionBadge
            asBadge={false}
            activeOption={params.value}
            options={convertValueAndColorToMultiDisplayColor(ISSUE_STATES_OPTIONS)}
          />
        ),
      },
    ],
    [dispatch],
  );

  const deduplicatedIssues = useMemo(
    () => issues.filter((issue, index, self) => self.findIndex((t) => t._id === issue._id) === index),
    [issues],
  );

  return (
    <ViewEditTable
      gridOptions={{
        // AutoHeight gör att tabellen växer och sidan (ScrollArea) blir scrollbar
        domLayout: "autoHeight",
        pagination: true,
        paginationPageSize: 20,
      }}
      data={deduplicatedIssues}
      columns={columns}
      onSave={() => {}}
      newItemTemplateObject={null}
    />
  );
};
